import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { Upload, ShoppingCart, ClipboardList, Trash2, Search, Plus, X } from "lucide-react";

import { OFFICIAL_CARDS, OFFICIAL_CARD_BY_CODE, SET_NAMES } from "@/lib/tcg-cards";
import { CardImage } from "@/components/CardImage";
import { useCardPrices } from "@/lib/use-card-prices";
import { tcgplayerAffiliateUrl } from "@/lib/tcgplayer-affiliate";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, toolSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { SNIPPETS } from "@/lib/snippet-copy";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { ShareButton } from "@/components/ShareButton";
import { pageTitle } from "@/lib/seo-title";
import { cardLinkProps } from "@/lib/card-link";

const URL = "https://palstars.com/tcg-bulk-order";

const FAQS: FaqItem[] = [
  {
    q: "How do I bulk order Palworld TCG cards by card number?",
    a: "Paste your list of printed Palworld card numbers — one per line, optionally with a quantity such as \"2 EBP01-001\" — into the box on this page. Pal Stars matches every code to its official card name and set, shows you a preview of the whole order, then hands the list to TCGplayer's Mass Entry cart so you check out once instead of adding cards one at a time.",
  },
  {
    q: "Can I import a deck list or CSV file of Palworld cards?",
    a: "Yes. Use the Import CSV button and upload any .csv or .txt export that contains a card number column — most collection trackers, deck builders and spreadsheet exports work as-is. Quantities are read from a quantity/qty/count column when present, otherwise each row counts as one copy.",
  },
  {
    q: "Does the bulk order tool cost anything?",
    a: "No. The builder is free and runs entirely in your browser — nothing is stored on Pal Stars. You pay only TCGplayer's own prices at checkout, and Pal Stars may earn an affiliate commission on the order at no extra cost to you.",
  },
  {
    q: "What happens to card numbers TCGplayer does not stock?",
    a: "Unmatched or unstocked printings stay visible in the preview and are flagged before you order, so you can drop them, swap a parallel for its base printing, or check the card's own Pal Stars price page for a different marketplace.",
  },
  {
    q: "Can I order a whole Palworld deck at once?",
    a: "Yes — paste the deck's card numbers with quantities and the preview totals every copy. A 50-card deck list becomes a single TCGplayer Mass Entry cart, which is the fastest way to buy a full Palworld OFFICIAL CARD GAME deck as singles.",
  },
];

export const Route = createFileRoute("/tcg-bulk-order")({
  head: () => ({
    meta: [
      { title: pageTitle("Palworld TCG Bulk Card Order — Deck List & CSV") },
      {
        name: "description",
        content:
          "Paste Palworld card numbers or import a CSV, preview the full order with names, sets and quantities, then bulk buy every single in one TCGplayer Mass Entry checkout.",
      },
      { property: "og:title", content: "Palworld TCG Bulk Card Order Builder" },
      {
        property: "og:description",
        content: "Drop a list of Palworld card numbers or a CSV and bulk order the whole deck through TCGplayer in one checkout.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["TCG Price Chart", "/prices"],
        ["Bulk Card Order", "/tcg-bulk-order"],
      ]),
      toolSchema({
        name: "Palworld TCG Bulk Card Order Builder",
        description:
          "Free tool that turns a list of printed Palworld card numbers or a CSV import into a single TCGplayer bulk checkout.",
        path: "/tcg-bulk-order",
      }),
      faqSchema(FAQS, URL),
      snippetAnswerSchema({ question: SNIPPETS.bulkOrder.question, answer: SNIPPETS.bulkOrder.answer, path: "/tcg-bulk-order" }),
    ],
  }),
  // The homepage bulk-list box hands its pasted lines over as ?list=…
  validateSearch: (search: Record<string, unknown>): { list?: string } =>
    typeof search.list === "string" ? { list: search.list.slice(0, 4000) } : {},
  component: BulkOrderPage,
});

interface OrderLine {
  raw: string;
  code: string | null;
  name: string;
  set: string;
  /** Printed rarity code, shown next to the scan so buyers spot parallels. */
  rarity: string;
  /** Official card scan, so the preview shows the card, not just its number. */
  image: string;
  qty: number;
}

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

const CODE_RE = /\b(E?(?:BP|TD|PR|SOUL)[A-Z0-9]*-?\d{2,3}[A-Z]{0,4})\b/i;

/** Resolve a printed code the way the rest of the site does: with or without the leading E. */
function findCard(token: string) {
  const upper = token.toUpperCase().replace(/\s+/g, "");
  return (
    OFFICIAL_CARD_BY_CODE.get(upper) ??
    OFFICIAL_CARD_BY_CODE.get(`E${upper}`) ??
    OFFICIAL_CARDS.find((c) => c.code.toUpperCase() === upper || c.code.toUpperCase() === `E${upper}`) ??
    null
  );
}

/** One text line -> one order line. Accepts "EBP01-001", "2 EBP01-001", "EBP01-001 x3", CSV rows. */
function parseLine(raw: string): OrderLine | null {
  const line = raw.trim();
  if (!line) return null;
  if (/^(qty|quantity|count|card|code|number)\b/i.test(line) && !CODE_RE.test(line)) return null; // header row

  const cells = line.split(/[,;\t|]/).map((c) => c.trim());
  const codeCell = cells.find((c) => CODE_RE.test(c)) ?? line;
  const codeMatch = codeCell.match(CODE_RE);

  let qty = 1;
  const lead = line.match(/^(\d{1,2})\s*[xX]?[\s,;\t|]/);
  const trail = line.match(/[xX]\s*(\d{1,2})\s*$/);
  const qtyCell = cells.find((c) => /^\d{1,2}$/.test(c) && c !== codeCell);
  if (lead) qty = Number(lead[1]);
  else if (trail) qty = Number(trail[1]);
  else if (qtyCell) qty = Number(qtyCell);
  qty = Math.min(99, Math.max(1, qty || 1));

  if (!codeMatch) return { raw: line, code: null, name: "", set: "", rarity: "", image: "", qty };
  const card = findCard(codeMatch[1]);
  if (!card) return { raw: line, code: codeMatch[1].toUpperCase(), name: "", set: "", rarity: "", image: "", qty };
  return {
    raw: line,
    code: card.code,
    name: card.name,
    set: SET_NAMES[card.set] ?? card.set,
    rarity: card.rarity,
    image: card.image,
    qty,
  };
}

/**
 * TCGplayer Mass Entry cart. `c` takes one "<qty> <card name>" per line, so a
 * whole deck arrives in the buyer's cart in a single hop, with our affiliate
 * tracking wrapped around it.
 */
function massEntryUrl(lines: OrderLine[]): string {
  const body = lines
    .filter((l) => l.name)
    .map((l) => `${l.qty} ${l.name} ${l.code ?? ""}`.trim())
    .join("||");
  const dest = `https://www.tcgplayer.com/massentry?productline=Palworld&c=${encodeURIComponent(body)}`;
  return tcgplayerAffiliateUrl(dest, "bulk-order");
}

function BulkOrderPage() {
  const { list } = Route.useSearch();
  const [text, setText] = useState(list ?? "");
  const [preview, setPreview] = useState<OrderLine[] | null>(null);
  const [search, setSearch] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  // Same ["price-board"] query the Palworld TCG price chart renders from, so the
  // preview never shows a value that disagrees with /prices.
  const { byCode, updated } = useCardPrices();

  // Card-database search: same official printings the price chart uses, so a
  // buyer who knows the Pal but not the printed number can still build a list.
  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return OFFICIAL_CARDS.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    ).slice(0, 8);
  }, [search]);

  const addCode = (code: string) => {
    setText((prev) => (prev.trim() ? `${prev.trim()}\n1 ${code}` : `1 ${code}`));
    setPreview(null);
  };

  /** Drop one line from the list (and from the open preview) when a buyer changes their mind. */
  const removeLine = (raw: string) => {
    setText((prev) =>
      prev
        .split(/\r?\n/)
        .filter((l) => l.trim() !== raw.trim())
        .join("\n"),
    );
    setPreview((prev) => (prev ? prev.filter((l) => l.raw.trim() !== raw.trim()) : prev));
  };

  /** Remove every line that resolves to this printed card code. */
  const removeCode = (code: string) => {
    setText((prev) =>
      prev
        .split(/\r?\n/)
        .filter((l) => {
          const parsed = parseLine(l);
          return !parsed || parsed.code?.toUpperCase() !== code.toUpperCase();
        })
        .join("\n"),
    );
    setPreview((prev) => (prev ? prev.filter((l) => l.code?.toUpperCase() !== code.toUpperCase()) : prev));
  };

  const parsed = useMemo(() => text.split(/\r?\n/).map(parseLine).filter((l): l is OrderLine => l !== null), [text]);
  const lines = preview ?? [];
  const matched = lines.filter((l) => l.name);
  const unmatched = lines.filter((l) => !l.name);
  const copies = matched.reduce((a, l) => a + l.qty, 0);
  const estTotal = matched.reduce((a, l) => {
    const row = l.code ? byCode.get(l.code.toUpperCase()) : undefined;
    return a + (row?.market ?? 0) * l.qty;
  }, 0);
  const priced = matched.filter((l) => l.code && byCode.get(l.code.toUpperCase())?.market).length;

  const onFile = async (file: File) => {
    const raw = await file.text();
    setText((prev) => (prev.trim() ? `${prev.trim()}\n${raw.trim()}` : raw.trim()));
    setPreview(null);
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link> · <Link to="/prices" className="hover:text-primary">TCG Price Chart</Link> · <span>Bulk Card Order</span>
      </nav>

      <header className="text-center">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Bulk Card Order</h1>
          <ShareButton
            title="Palworld TCG Bulk Card Order — Pal Stars"
            text="Paste Palworld card numbers or import a CSV, preview the order, then bulk buy through TCGplayer."
            path="/tcg-bulk-order"
          />
        </div>
        <p className="aeo-answer mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
          Drop a list of printed Palworld card numbers — or import a CSV — preview the full order, then buy every single
          in one TCGplayer checkout instead of adding cards one at a time.
        </p>
      </header>

      <SnippetFunnel className="mt-6" {...SNIPPETS.bulkOrder} />

      <section className="glass mx-auto mt-8 max-w-2xl rounded-2xl p-6">
        <label htmlFor="bulk-list" className="text-sm font-semibold">
          Card numbers
        </label>
        <p className="mt-1 text-xs text-muted-foreground">
          One per line. Quantities optional: <code>EBP01-001</code>, <code>2 EBP01-001</code> or <code>EBP01-001 x3</code>.
        </p>
        <textarea
          id="bulk-list"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setPreview(null);
          }}
          onDrop={async (e) => {
            const file = e.dataTransfer.files?.[0];
            if (!file) return;
            e.preventDefault();
            await onFile(file);
          }}
          rows={9}
          spellCheck={false}
          placeholder={"2 EBP01-001\n1 EBP01-025SSP\nETD01-012TSP\nESOUL-002"}
          className="mt-3 w-full rounded-xl border border-border/60 bg-card/40 p-3 font-mono text-sm outline-none focus:border-primary/60"
        />

        <div className="mt-5 border-t border-border/50 pt-4">
          <label htmlFor="bulk-search" className="text-sm font-semibold">
            Or search the Palworld card database
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            Search by card name or printed number, then add it to the list above.
          </p>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input
              id="bulk-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cattiva, EBP01-001, Soul…"
              className="w-full rounded-xl border border-border/60 bg-card/40 py-2 pl-9 pr-3 text-sm outline-none focus:border-primary/60"
            />
          </div>
          {search.trim().length >= 2 ? (
            <ul className="mt-3 divide-y divide-border/50 text-sm" aria-live="polite">
              {results.length === 0 ? (
                <li className="py-2 text-xs text-muted-foreground">No printed Palworld card matches that search.</li>
              ) : (
                results.map((c) => {
                  // Same ["price-board"] row the price chart uses, so the search
                  // hit shows the identical art, rarity and market price.
                  const row = byCode.get(c.code.toUpperCase());
                  return (
                    <li key={c.code} className="flex items-center gap-3 py-2">
                      <CardImage
                        src={row?.image ?? c.image}
                        alt={`${c.name} ${c.code} Palworld card`}
                        width={40}
                        height={56}
                        className="shrink-0 rounded"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold">{c.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {c.code} · {SET_NAMES[c.set] ?? c.set}
                          {c.rarity ? ` · ${c.rarity}` : ""}
                          {row?.market ? ` · $${row.market.toFixed(2)}` : ""}
                        </span>
                      </span>
                      {parsed.some((l) => l.code?.toUpperCase() === c.code.toUpperCase()) ? (
                        <button
                          type="button"
                          onClick={() => removeCode(c.code)}
                          aria-label={`Remove ${c.name} ${c.code} from the order list`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-destructive/50 px-3 py-1.5 text-xs font-semibold text-destructive hover:border-destructive"
                        >
                          <X className="h-3.5 w-3.5" aria-hidden="true" /> Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => addCode(c.code)}
                          aria-label={`Add ${c.name} ${c.code} to the order list`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border/60 px-3 py-1.5 text-xs font-semibold hover:border-primary/60"
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
                        </button>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await onFile(file);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-semibold hover:border-primary/60"
          >
            <Upload className="h-4 w-4" aria-hidden="true" /> Import CSV
          </button>
          <button
            type="button"
            onClick={() => setPreview(parsed)}
            disabled={parsed.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-primary/20 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/30 disabled:opacity-40"
          >
            <ClipboardList className="h-4 w-4" aria-hidden="true" /> Preview order ({parsed.length})
          </button>
          {text ? (
            <button
              type="button"
              onClick={() => {
                setText("");
                setPreview(null);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground hover:border-primary/60"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Clear
            </button>
          ) : null}
        </div>
      </section>

      {preview ? (
        <section className="glass mx-auto mt-6 max-w-2xl rounded-2xl p-6" aria-live="polite">
          <h2 className="text-lg font-bold">Order preview</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {matched.length} matched printing{matched.length === 1 ? "" : "s"} · {copies} total cop{copies === 1 ? "y" : "ies"}
            {unmatched.length ? ` · ${unmatched.length} unmatched line${unmatched.length === 1 ? "" : "s"}` : ""}
          </p>

          <ul className="mt-4 divide-y divide-border/50 text-sm">
            {matched.map((l, i) => {
              const row = l.code ? byCode.get(l.code.toUpperCase()) : undefined;
              const market = row?.market ?? null;
              return (
                <li key={`${l.code}-${i}`} className="flex items-center gap-3 py-3">
                  {l.image ? (
                    <Link {...cardLinkProps(l.code ?? "")} className="shrink-0">
                      <CardImage src={l.image} alt={`${l.name} ${l.code} Palworld card`} width={48} height={67} className="rounded" />
                    </Link>
                  ) : (
                    <span className="h-[67px] w-12 shrink-0 rounded bg-card/60" aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1">
                    <Link {...cardLinkProps(l.code ?? "")} className="font-semibold hover:text-primary">
                      {l.name}
                    </Link>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {l.code} · {l.set}
                      {l.rarity ? ` · ${l.rarity}` : ""}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {market ? `Market ${fmtUsd(market)} each` : "Market value not tracked yet"}
                      {row?.listings ? ` · ${row.listings} listings` : ""}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block tabular-nums text-muted-foreground">x{l.qty}</span>
                    {market ? <span className="block tabular-nums text-xs font-semibold">{fmtUsd(market * l.qty)}</span> : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeLine(l.raw)}
                    aria-label={`Remove ${l.name} ${l.code ?? ""} from the order`}
                    title="Remove from order"
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border/60 px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:border-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="sr-only sm:not-sr-only">Remove</span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex items-center justify-between rounded-xl border border-border/60 p-3 text-sm">
            <span className="text-muted-foreground">
              Estimated market value{priced < matched.length ? ` (${priced} of ${matched.length} cards priced)` : ""}
            </span>
            <span className="font-bold tabular-nums">{estTotal ? fmtUsd(estTotal) : "—"}</span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Card details and market values come from the{" "}
            <Link to="/prices" className="text-primary hover:underline">Palworld TCG price chart</Link>
            {updated ? ` (updated ${new Date(updated).toISOString().slice(0, 16).replace("T", " ")} UTC)` : ""}. TCGplayer
            seller prices at checkout may differ.
          </p>

          {unmatched.length ? (
            <div className="mt-4 rounded-xl border border-border/60 p-3 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground">Not recognised</p>
              <p className="mt-1">These lines do not match a printed Palworld card number and are left out of the cart:</p>
              <ul className="mt-2 space-y-1">
                {unmatched.map((l, i) => (
                  <li key={`${l.raw}-${i}`} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate font-mono">{l.raw}</span>
                    <button
                      type="button"
                      onClick={() => removeLine(l.raw)}
                      aria-label={`Remove ${l.raw} from the list`}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border/60 px-2 py-1 font-semibold hover:border-destructive hover:text-destructive"
                    >
                      <X className="h-3 w-3" aria-hidden="true" /> Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <a
            href={massEntryUrl(matched)}
            target="_blank"
            rel="nofollow ugc noopener noreferrer"
            onClick={() => trackAffiliateClick({ source: "tcgplayer", kind: "card", targetKey: "bulk-order" })}
            className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white ${
              matched.length ? "" : "pointer-events-none opacity-40"
            }`}
            style={{ backgroundColor: "#16A34A" }}
          >
            <ShoppingCart className="h-4 w-4" aria-hidden="true" /> Order {copies} card{copies === 1 ? "" : "s"} on TCGplayer
          </a>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            Opens TCGplayer's Mass Entry cart with every matched printing pre-filled. Review quantities and sellers there
            before paying.
          </p>
        </section>
      ) : null}

      <div className="mx-auto mt-8 max-w-2xl">
        <AffiliateDisclosure />
      </div>

      <FaqSection items={FAQS} />

      <nav aria-label="Price the list before you buy" className="glass mt-8 rounded-2xl p-6">
        <h2 className="text-xl font-bold">Price the list before you buy</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            { to: "/prices", label: "Palworld TCG price chart", note: "Market value for every printed card number." },
            { to: "/tcg-price-index", label: "Palworld TCG Price Index", note: "Is the market rising or falling right now?" },
            { to: "/deck-builder", label: "Palworld deck builder", note: "Build the deck, then export the card numbers." },
            { to: "/collection", label: "Collection tracker", note: "Log what you bought and track profit." },
          ].map((l) => (
            <li key={l.to} className="rounded-lg border border-border/60 p-3">
              <Link to={l.to} className="text-sm font-semibold text-primary hover:underline">
                {l.label}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{l.note}</p>
            </li>
          ))}
        </ul>
      </nav>
    </main>
  );
}
